import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Save, RotateCcw, Boxes, User, Heart, Wrench, Coins, FileDown, ExternalLink, Sparkles, Waves, Compass, KeyRound } from "lucide-react";
import { toast } from "sonner";

const EXTERNAL_TOOLS = [
  {
    href: "https://awake.wearetheweave.co.uk",
    label: "Awake",
    tagline: "The Weave's daily clarity tool",
    description: "Wake up the strategic thinking. A focused prompt-driven workspace to sharpen the day's most important question before diving into the work.",
    icon: Sparkles,
    accent: "#2596be",
  },
  {
    href: "https://catch.wearetheweave.co.uk",
    label: "Catch the Wave",
    tagline: "Horizon scan · PESTLE",
    description: "A PESTLE-model horizon scan that complements a sector analysis — surfacing Political, Economic, Social, Technological, Legal and Environmental signals shaping the landscape. Runs on each teammate's own email, so if the team needs another pass, a different member can just re-run it with their own email.",
    icon: Waves,
    accent: "#f4b847",
  },
  {
    href: "https://pisa.wearetheweave.co.uk",
    label: "PISA",
    tagline: "The Weave's diagnostic framework",
    description: "Pinpoint where a business stands today and where the leverage sits — a structured diagnostic that frames strategic choices in an M&A context.",
    icon: Compass,
    accent: "#0A0A0A",
  },
  {
    href: "https://opportunity-unlock.wearetheweave.co.uk",
    label: "Opportunity Unlock",
    tagline: "Find the next big thing",
    description: "A 10-minute grounded diagnostic for founders whose sales have plateaued. Surfaces one customer segment, one persona, and one MVP to test — sized bottom-up with accredited public data (ONS, Gov.uk, CIA Factbook, YouGov) and a 90-day plan.",
    icon: KeyRound,
    accent: "#A23232",
  },
];

// -------- Framework configs (derived from supplied PDFs / docx) --------
const BMC_BLOCKS = [
  { key: "key_partners", label: "Key Partners",
    help: "Strategic alliances, suppliers, joint ventures. Who augments your offer?" },
  { key: "key_activities", label: "Key Activities",
    help: "Production · Problem-solving · Platform. What must the business actually do?" },
  { key: "key_resources", label: "Key Resources",
    help: "Tangible (finance, machinery), intellectual (know-how), social (network)." },
  { key: "value_propositions", label: "Value Propositions",
    help: "What job does it do for the customer? What pain do you ease, what gain do you create?" },
  { key: "customer_relationships", label: "Customer Relationships",
    help: "1:1 consultancy · platform · subscription · membership. Match to the segment." },
  { key: "channels", label: "Channels",
    help: "Awareness, comparison, decision, after-sales — every touchpoint of the journey." },
  { key: "customer_segments", label: "Customer Segments",
    help: "Who exactly are you serving? Pick the most receptive segment first." },
  { key: "cost_structure", label: "Cost Structure",
    help: "Where is the bulk of expenditure? People, systems, marketing? Align to brand value." },
  { key: "revenue_streams", label: "Revenue Streams",
    help: "Asset sale · subscription · licensing · membership. Match to perceived value." },
];

const AVATAR_SECTIONS = [
  {
    title: "Identity",
    fields: [
      { key: "name", label: "Persona name", placeholder: "Give them a fictional name" },
      { key: "tagline", label: "One-line summary", placeholder: "A complete overview of them in a sentence" },
    ],
  },
  {
    title: "Demographics",
    fields: [
      { key: "location", label: "Location" },
      { key: "age", label: "Age" },
      { key: "income", label: "Household income" },
      { key: "social_status", label: "How they see their social status" },
      { key: "gender", label: "How they define themselves (gender)" },
    ],
    columns: 2,
  },
  {
    title: "Bio",
    fields: [{ key: "bio", label: "Back story", textarea: true,
      placeholder: "A day in the life. Their context, their world." }],
  },
  {
    title: "Profile & lifestyle",
    fields: [
      { key: "personality", label: "Personality, interests, hobbies", textarea: true },
      { key: "transport_holiday", label: "Car, transport, holidays", textarea: true },
      { key: "day_in_life", label: "A day in their life", textarea: true },
    ],
  },
  {
    title: "Attitudes",
    fields: [
      { key: "worldviews", label: "Worldviews, biases, heroes", textarea: true },
      { key: "tradition_freewheel", label: "Tradition vs free-wheeler vs problem-solver", textarea: true },
      { key: "family", label: "Family loyalties", textarea: true },
    ],
  },
  {
    title: "Motivators & objectives",
    fields: [
      { key: "awake_at_night", label: "What keeps them awake at night?", textarea: true },
      { key: "out_of_bed", label: "What gets them out of bed?", textarea: true },
      { key: "big_change", label: "If they could create one big change…", textarea: true },
      { key: "priority_distraction", label: "Biggest priority vs biggest distraction", textarea: true },
    ],
  },
  {
    title: "Pain & relationship to your offer",
    fields: [
      { key: "pain", label: "What pain do they want you to solve?", textarea: true },
      { key: "why_you", label: "Why have they come to you?", textarea: true },
      { key: "state", label: "What state are they in when they arrive?", textarea: true },
      { key: "pain_severity", label: "How painful is the problem to them?", textarea: true },
      { key: "trust_in_solution", label: "Why do they think your solution will solve this?", textarea: true },
    ],
  },
  {
    title: "Alternatives & competitors",
    fields: [
      { key: "alternatives", label: "What have they tried so far?", textarea: true },
      { key: "competitors", label: "Are they aware of competitors? Which? Would they recommend any?", textarea: true },
      { key: "why_above_alternatives", label: "Why do they choose you over the alternatives?", textarea: true },
    ],
  },
  {
    title: "Buying journey",
    fields: [
      { key: "stand_out", label: "What makes you stand out?", textarea: true },
      { key: "real_value", label: "Real value of your offer to them", textarea: true },
      { key: "why_love", label: "Why do they really love what you do?", textarea: true },
      { key: "buying_qualification", label: "What do they need to do to qualify as a customer?", textarea: true },
      { key: "sacrifice", label: "What are they sacrificing (time, money, reputation)?", textarea: true },
      { key: "channels_to_find", label: "Where can you find them? Social channels, communities", textarea: true },
      { key: "decision_style", label: "Impulsive or considered? Price or value?", textarea: true },
      { key: "influencers", label: "Who influences them?", textarea: true },
      { key: "barriers", label: "Purchasing barriers", textarea: true },
    ],
  },
  {
    title: "Post-purchase",
    fields: [
      { key: "use_product", label: "How will they use it?", textarea: true },
      { key: "stay_connected", label: "Why would they stay connected?", textarea: true },
      { key: "extend_journey", label: "How can you extend the journey / upsell?", textarea: true },
      { key: "complaints", label: "How do they complain — and how do you handle it?", textarea: true },
    ],
  },
];

const DESIRABILITY_FACTORS = [
  "Addresses a real user need",
  "Fits into daily life / workflow",
  "Easy & intuitive to use",
  "Creates positive emotions",
  "Stronger than competing alternatives",
  "Users would recommend it",
  "Aligns with cultural & social norms",
  "Overcomes adoption barriers",
];

const FEASIBILITY_FACTORS = [
  "Technical feasibility",
  "Operational feasibility",
  "Financial feasibility",
  "Resource feasibility",
  "Scalability feasibility",
  "Legal & compliance feasibility",
  "Cultural & ethical feasibility",
];

const VIABILITY_FACTORS = [
  "Revenue model clarity",
  "Cost structure & sustainability",
  "Profitability & breakeven potential",
  "Market demand & growth",
  "Competitive advantage",
  "Customer willingness to pay",
  "Scalability potential",
  "Alignment with business strategy",
  "Risk factors (financial / operational)",
  "Regulatory & compliance risks",
  "Sustainability & ethical considerations",
];

const FRAMEWORKS = {
  bmc: { label: "Business Model Canvas", icon: Boxes, layout: "bmc" },
  customer_avatar: { label: "Customer Avatar", icon: User, layout: "avatar" },
  desirability: { label: "Desirability Audit", icon: Heart, layout: "audit", factors: DESIRABILITY_FACTORS,
    intro: "Does the solution solve a meaningful problem in a way users find valuable, engaging and intuitive? Score 1 (low) to 5 (high)." },
  feasibility: { label: "Feasibility Audit", icon: Wrench, layout: "audit", factors: FEASIBILITY_FACTORS,
    intro: "Can the solution actually be built, run and scaled? Score 1 (major obstacles) to 5 (no issues)." },
  viability: { label: "Viability Audit", icon: Coins, layout: "audit", factors: VIABILITY_FACTORS,
    intro: "Is the solution financially sustainable and strategically aligned? Score 1 (not viable) to 5 (highly viable)." },
};

const blankAuditData = (factors) => ({
  matrix: factors.map((f) => ({ factor: f, score: 0, notes: "" })),
  swot: { strengths: "", weaknesses: "", opportunities: "", threats: "" },
  context: "",
  decision: "",
});

const blankDataFor = (type) => {
  if (type === "bmc") return Object.fromEntries(BMC_BLOCKS.map((b) => [b.key, ""]));
  if (type === "customer_avatar") {
    const out = {};
    AVATAR_SECTIONS.forEach((s) => s.fields.forEach((f) => { out[f.key] = ""; }));
    return out;
  }
  if (type === "desirability") return blankAuditData(DESIRABILITY_FACTORS);
  if (type === "feasibility") return blankAuditData(FEASIBILITY_FACTORS);
  if (type === "viability") return blankAuditData(VIABILITY_FACTORS);
  return {};
};

export default function Frameworks() {
  const { user } = useAuth();
  const [active, setActive] = useState("bmc");
  const [scope, setScope] = useState(user?.team_id ? "team" : "personal");
  const [docs, setDocs] = useState({}); // by type
  const [drafts, setDrafts] = useState({}); // by type
  const [saving, setSaving] = useState(false);

  const teamId = scope === "team" ? user?.team_id : null;

  const load = async () => {
    const updates = {};
    for (const t of Object.keys(FRAMEWORKS)) {
      try {
        const { data } = await api.get(`/frameworks/${t}`, { params: teamId ? { team_id: teamId } : {} });
        updates[t] = data;
      } catch {
        updates[t] = { framework_type: t, data: {}, exists: false };
      }
    }
    setDocs(updates);
    const dr = {};
    Object.entries(updates).forEach(([t, d]) => {
      dr[t] = { ...blankDataFor(t), ...(d?.data || {}) };
    });
    setDrafts(dr);
  };

  useEffect(() => { load(); }, [teamId]);

  const setField = (type, key, value) =>
    setDrafts((s) => ({ ...s, [type]: { ...s[type], [key]: value } }));

  const setMatrix = (type, idx, patch) =>
    setDrafts((s) => {
      const m = [...(s[type].matrix || [])];
      m[idx] = { ...m[idx], ...patch };
      return { ...s, [type]: { ...s[type], matrix: m } };
    });

  const setSwot = (type, key, value) =>
    setDrafts((s) => ({ ...s, [type]: { ...s[type], swot: { ...s[type].swot, [key]: value } } }));

  const save = async (type) => {
    setSaving(true);
    try {
      await api.put("/frameworks", { team_id: teamId, framework_type: type, data: drafts[type] });
      toast.success(`${FRAMEWORKS[type].label} saved`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const reset = (type) => {
    setDrafts((s) => ({ ...s, [type]: { ...blankDataFor(type), ...(docs[type]?.data || {}) } }));
  };

  const config = FRAMEWORKS[active];
  const canEdit = scope === "personal" || (user?.team_id && scope === "team");
  const lastSaved = docs[active]?.updated_at;
  const lastSavedBy = docs[active]?.updated_by_name;

  return (
    <div className="space-y-8" data-testid="frameworks-page">
      <header className="space-y-2">
        <div className="label-eyebrow">Native framework tools</div>
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter">Build the case.</h1>
        <p className="text-sm text-[#525252] max-w-2xl">
          Five tools to interrogate your investment case: model the business (BMC), profile the customer, then
          stress-test it through the Desirability / Feasibility / Viability lens. Saves auto-load and persist.
        </p>
      </header>

      {/* External Weave tools */}
      <section className="space-y-3" data-testid="frameworks-external-tools">
        <div className="label-eyebrow">The Weave · companion tools</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {EXTERNAL_TOOLS.map((t) => {
            const Icon = t.icon;
            return (
              <a
                key={t.href}
                href={t.href}
                target="_blank"
                rel="noreferrer"
                className="group block border-2 border-[#0A0A0A] bg-white p-5 hover:bg-[#FAFAFA] transition-colors"
                data-testid={`frameworks-external-${t.label.toLowerCase()}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-10 h-10 flex items-center justify-center text-white"
                      style={{ backgroundColor: t.accent }}
                    >
                      <Icon size={18} />
                    </div>
                    <div>
                      <div className="font-display text-2xl font-black tracking-tighter">{t.label}</div>
                      <div className="label-eyebrow" style={{ color: t.accent }}>{t.tagline}</div>
                    </div>
                  </div>
                  <ExternalLink size={16} className="text-[#525252] group-hover:text-[#0A0A0A] transition-colors mt-1" />
                </div>
                <p className="text-xs text-[#525252] mt-3 leading-relaxed">{t.description}</p>
                <div className="mt-3 text-[10px] uppercase tracking-wider text-[#525252]">
                  {t.href.replace(/^https?:\/\//, "")}
                </div>
              </a>
            );
          })}
        </div>
      </section>

      {/* Scope toggle */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="label-eyebrow">Save scope</div>
        <div className="grid grid-cols-2 border border-[#0A0A0A]" data-testid="frameworks-scope-toggle">
          <button
            onClick={() => setScope("personal")}
            data-testid="frameworks-scope-personal"
            className={`px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] transition-colors ${
              scope === "personal" ? "bg-[#0A0A0A] text-white" : "bg-white text-[#0A0A0A] hover:bg-[#F3F4F6]"
            }`}
          >My draft</button>
          <button
            onClick={() => user?.team_id && setScope("team")}
            disabled={!user?.team_id}
            data-testid="frameworks-scope-team"
            className={`px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] transition-colors ${
              scope === "team" ? "bg-[#0A0A0A] text-white" : "bg-white text-[#0A0A0A] hover:bg-[#F3F4F6] disabled:opacity-40"
            }`}
          >Team {user?.team_id ? "" : "(join a team first)"}</button>
        </div>
        {lastSaved && (
          <span className="text-xs text-[#525252]">
            Last saved {new Date(lastSaved).toLocaleString()} {lastSavedBy ? `· ${lastSavedBy}` : ""}
          </span>
        )}
      </div>

      {/* Framework tabs */}
      <div className="flex flex-wrap gap-2 border-b border-[#E5E7EB]">
        {Object.entries(FRAMEWORKS).map(([key, f]) => {
          const Icon = f.icon;
          const isActive = active === key;
          return (
            <button
              key={key}
              data-testid={`framework-tab-${key}`}
              onClick={() => setActive(key)}
              className={`flex items-center gap-2 px-4 py-2.5 text-[12px] font-semibold uppercase tracking-[0.16em] border-b-2 transition-colors -mb-px ${
                isActive
                  ? "border-[#2596be] text-[#0A0A0A] bg-white"
                  : "border-transparent text-[#525252] hover:text-[#0A0A0A]"
              }`}
            >
              <Icon size={14} /> {f.label}
            </button>
          );
        })}
      </div>

      {/* Active framework */}
      {config.layout === "bmc" && (
        <BmcGrid draft={drafts.bmc} setField={(k, v) => setField("bmc", k, v)} canEdit={canEdit} />
      )}
      {config.layout === "avatar" && (
        <AvatarForm draft={drafts.customer_avatar}
                    setField={(k, v) => setField("customer_avatar", k, v)} canEdit={canEdit} />
      )}
      {config.layout === "audit" && (
        <AuditForm
          intro={config.intro}
          factors={config.factors}
          draft={drafts[active]}
          setMatrix={(idx, patch) => setMatrix(active, idx, patch)}
          setSwot={(k, v) => setSwot(active, k, v)}
          setField={(k, v) => setField(active, k, v)}
          canEdit={canEdit}
        />
      )}

      {/* Save bar */}
      <div className="flex flex-wrap items-center justify-end gap-3 sticky bottom-2 bg-[#FDFDFD]/95 backdrop-blur-sm py-3 border-t border-[#E5E7EB]">
        <Button
          variant="outline"
          onClick={() => reset(active)}
          disabled={!canEdit || saving}
          className="rounded-none border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#F3F4F6] uppercase tracking-wide text-xs font-semibold"
          data-testid="framework-reset-btn"
        ><RotateCcw size={14} className="mr-2" /> Reset to saved</Button>
        <Button
          onClick={() => save(active)}
          disabled={!canEdit || saving}
          className="rounded-none bg-[#2596be] hover:bg-[#1e7da3] uppercase tracking-wide text-xs font-semibold"
          data-testid="framework-save-btn"
        ><Save size={14} className="mr-2" /> {saving ? "Saving…" : "Save"}</Button>
      </div>
    </div>
  );
}

// ---------- BMC ----------
function BmcGrid({ draft = {}, setField, canEdit }) {
  // BMC traditional 5×3 layout: row1 = key_partners | key_activities + key_resources | value_propositions | customer_relationships + channels | customer_segments
  // For simplicity & responsiveness we show 9 cards in a grid, ordered logically.
  const ordered = ["key_partners", "key_activities", "key_resources", "value_propositions",
                   "customer_relationships", "channels", "customer_segments", "cost_structure", "revenue_streams"];
  const map = Object.fromEntries(BMC_BLOCKS.map((b) => [b.key, b]));
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="framework-bmc">
      {ordered.map((k) => {
        const b = map[k];
        const span = (k === "value_propositions" || k === "customer_segments") ? "md:row-span-1" : "";
        return (
          <div key={k} className={`border-2 border-[#0A0A0A] p-4 bg-white ${span}`}>
            <div className="label-eyebrow !text-[#2596be]">{b.label}</div>
            <p className="text-[11px] text-[#525252] mt-1 mb-2 leading-snug">{b.help}</p>
            <Textarea
              value={draft[k] || ""}
              onChange={(e) => setField(k, e.target.value)}
              disabled={!canEdit}
              className="rounded-none border-[#E5E7EB] min-h-[110px] text-sm"
              placeholder="Notes, stickies, key ideas…"
              data-testid={`framework-bmc-${k}`}
            />
          </div>
        );
      })}
    </div>
  );
}

// ---------- Avatar ----------
function AvatarForm({ draft = {}, setField, canEdit }) {
  return (
    <div className="space-y-8" data-testid="framework-avatar">
      {AVATAR_SECTIONS.map((s) => (
        <section key={s.title} className="border border-[#E5E7EB] bg-white p-5">
          <div className="label-eyebrow !text-[#2596be]">{s.title}</div>
          <div className={`mt-4 grid gap-4 ${s.columns === 2 ? "md:grid-cols-2" : "grid-cols-1"}`}>
            {s.fields.map((f) => (
              <div key={f.key}>
                <Label className="text-xs text-[#525252]">{f.label}</Label>
                {f.textarea ? (
                  <Textarea
                    value={draft[f.key] || ""}
                    onChange={(e) => setField(f.key, e.target.value)}
                    disabled={!canEdit}
                    placeholder={f.placeholder || ""}
                    className="rounded-none mt-1 min-h-[80px] text-sm"
                    data-testid={`framework-avatar-${f.key}`}
                  />
                ) : (
                  <Input
                    value={draft[f.key] || ""}
                    onChange={(e) => setField(f.key, e.target.value)}
                    disabled={!canEdit}
                    placeholder={f.placeholder || ""}
                    className="rounded-none mt-1 h-10 text-sm"
                    data-testid={`framework-avatar-${f.key}`}
                  />
                )}
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

// ---------- Audit (Desirability / Feasibility / Viability) ----------
function AuditForm({ intro, factors, draft = {}, setMatrix, setSwot, setField, canEdit }) {
  const matrix = draft.matrix || [];
  const swot = draft.swot || {};
  const total = matrix.reduce((sum, m) => sum + (Number(m.score) || 0), 0);
  const max = factors.length * 5;
  const avg = matrix.length ? (total / matrix.length).toFixed(1) : "—";

  return (
    <div className="space-y-6" data-testid="framework-audit">
      <p className="text-sm text-[#525252] max-w-3xl">{intro}</p>

      <section className="border border-[#E5E7EB] bg-white">
        <div className="px-5 py-3 border-b border-[#E5E7EB] flex items-center justify-between">
          <div className="label-eyebrow !text-[#2596be]">Assessment matrix</div>
          <div className="text-xs text-[#525252]">
            Total <span className="font-semibold text-[#0A0A0A]">{total}</span> / {max} ·
            average <span className="font-semibold text-[#0A0A0A]">{avg}</span> / 5
          </div>
        </div>
        <div className="divide-y divide-[#E5E7EB]">
          {matrix.map((row, idx) => (
            <div key={row.factor} className="grid grid-cols-12 gap-3 p-4 items-start">
              <div className="col-span-12 md:col-span-4 text-sm font-medium text-[#0A0A0A] pt-2">
                {row.factor}
              </div>
              <div className="col-span-6 md:col-span-3">
                <ScoreSelector
                  value={row.score}
                  onChange={(v) => setMatrix(idx, { score: v })}
                  disabled={!canEdit}
                  testid={`audit-score-${idx}`}
                />
              </div>
              <div className="col-span-12 md:col-span-5">
                <Input
                  value={row.notes || ""}
                  onChange={(e) => setMatrix(idx, { notes: e.target.value })}
                  disabled={!canEdit}
                  placeholder="Notes / evidence"
                  className="rounded-none h-10 text-sm"
                  data-testid={`audit-notes-${idx}`}
                />
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="border border-[#E5E7EB] bg-white p-5">
        <div className="label-eyebrow !text-[#2596be]">SWOT analysis</div>
        <div className="grid md:grid-cols-2 gap-4 mt-4">
          {[
            ["strengths", "Strengths"], ["weaknesses", "Weaknesses"],
            ["opportunities", "Opportunities"], ["threats", "Threats"],
          ].map(([k, lbl]) => (
            <div key={k}>
              <Label className="text-xs text-[#525252]">{lbl}</Label>
              <Textarea
                value={swot[k] || ""}
                onChange={(e) => setSwot(k, e.target.value)}
                disabled={!canEdit}
                className="rounded-none mt-1 min-h-[100px] text-sm"
                data-testid={`audit-swot-${k}`}
              />
            </div>
          ))}
        </div>
      </section>

      <section className="border border-[#E5E7EB] bg-white p-5 space-y-4">
        <div>
          <Label className="text-xs text-[#525252]">Context / scope</Label>
          <Textarea
            value={draft.context || ""}
            onChange={(e) => setField("context", e.target.value)}
            disabled={!canEdit}
            className="rounded-none mt-1 min-h-[80px] text-sm"
            placeholder="What problem are we addressing? What assumptions underlie this audit?"
            data-testid="audit-context"
          />
        </div>
        <div>
          <Label className="text-xs text-[#525252]">Decision</Label>
          <Textarea
            value={draft.decision || ""}
            onChange={(e) => setField("decision", e.target.value)}
            disabled={!canEdit}
            className="rounded-none mt-1 min-h-[80px] text-sm"
            placeholder="High score (4-5): proceed. Medium (2-3): refine. Low (1): reconsider. Capture the call."
            data-testid="audit-decision"
          />
        </div>
      </section>
    </div>
  );
}

function ScoreSelector({ value, onChange, disabled, testid }) {
  return (
    <div className="flex gap-1" data-testid={testid}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => !disabled && onChange(value === n ? 0 : n)}
          disabled={disabled}
          className={`w-9 h-9 border text-sm font-semibold transition-colors ${
            value === n
              ? "bg-[#2596be] border-[#2596be] text-white"
              : "bg-white border-[#E5E7EB] text-[#525252] hover:border-[#0A0A0A]"
          } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
        >{n}</button>
      ))}
    </div>
  );
}
